import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, LifeBuoy, Loader2, Search, Ticket } from "lucide-react";
import { SupportDialog } from "@/components/support/SupportDialog";
import { InstallAppGuide } from "@/components/help/InstallAppGuide";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { categoryLabel, statusLabel } from "@/lib/support";
import {
  articlesByTopic,
  findArticle,
  helpTopics,
  popularArticles,
  searchHelp,
  type HelpArticle,
  type HelpTopicId,
} from "@/lib/help-articles";
import { adImageSrc } from "@/lib/image-src";
import { getGuestTicket, getMyTickets, replyGuestTicket } from "@/lib/support.functions";
import { useSurfaceWords } from "@/lib/surface";
import { useLocale } from "@/lib/i18n";
import { localizedPath } from "@/lib/i18n/slug-map";
import { supportPageText, type SupportPageCopy } from "@/lib/i18n/support-copy";
import type { Lang } from "@/lib/i18n/config";

export const Route = createFileRoute("/support")({
  // Enkel egen kontroll i stället för ett valideringsbibliotek – håller
  // sajtens startpaket litet.
  validateSearch: (
    search: Record<string, unknown>,
  ): { arende?: string; fraga?: string; amne?: string } => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      arende: str(search["arende"]),
      fraga: str(search["fraga"]),
      amne: str(search["amne"]),
    };
  },
  head: () => ({
    meta: [
      { title: "Hjälpcenter och support — Sellavo" },
      {
        name: "description",
        content:
          "Sök bland guider om konto, annonser, auktioner, nivåer och appen på mobilen – eller skicka ett ärende till Sellavos support.",
      },
      { property: "og:title", content: "Hjälpcenter och support — Sellavo" },
      {
        property: "og:description",
        content: "Guider, svar och support för Sellavo – med eller utan konto.",
      },
      { property: "og:url", content: "https://sellavo.com/support" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/support" }],
  }),
  component: SupportPage,
});

export function SupportPage() {
  const { lang } = useLocale();
  const tx = supportPageText[lang];
  const words = useSurfaceWords();
  const rawSearch = useSearch({ strict: false }) as {
    arende?: string;
    fraga?: string;
    amne?: string;
  };
  const { arende, fraga, amne } = rawSearch;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const topics = helpTopics(lang);
  const popular = popularArticles(lang);
  const results = useMemo(() => searchHelp(query, 20, lang), [query, lang]);
  const article = findArticle(fraga, lang);
  const topic = topics.find((t) => t.id === amne) ?? null;
  const topicName = (id: string) => topics.find((item) => item.id === id)?.label ?? tx.other;

  const setParam = (next: { fraga?: string; amne?: string }) => {
    setQuery("");
    void navigate({ to: localizedPath("/support", lang), search: { arende, ...next } });
  };

  return (
    <main className="bg-background pb-[calc(var(--tabbar-space)+2rem)]">
      <section className="bg-ocean-900 px-4 py-10 text-white sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-ocean-300">
            <LifeBuoy className="h-4 w-4" />
             {tx.eyebrow}
          </p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
             {tx.title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-ocean-100 sm:mt-5 sm:text-lg">
             {tx.introBefore} {words.here}. {tx.introAfter}
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">


      <div className="relative mt-6">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
           placeholder={tx.searchPlaceholder}
          className="h-12 pl-9"
           aria-label={tx.searchLabel}
        />
      </div>

      {user && <MyOpenTickets />}

      {query.trim().length > 1 ? (
           <SearchResults
           lang={lang}
           tx={tx}
           topicName={topicName}
          query={query}
          results={results}
          onOpen={(id) => setParam({ fraga: id })}
          onAsk={() => setOpen(true)}
        />
      ) : article ? (
         <ArticleView article={article} tx={tx} topicName={topicName} onBack={() => setParam({ amne: article.topic })} />
      ) : topic ? (
         <TopicView topic={topic.id} lang={lang} tx={tx} topicName={topicName} onOpen={(id) => setParam({ fraga: id })} onBack={() => setParam({})} />
      ) : (
        <>
          <section className="mt-8">
             <h2 className="font-heading text-lg font-bold text-foreground">{tx.popular}</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
               {popular.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setParam({ fraga: a.id })}
                  className="rounded-xl border border-border bg-card p-3 text-left text-sm font-semibold text-foreground transition-colors hover:bg-accent/50"
                >
                  {a.title}
                </button>
              ))}
            </div>
          </section>

          <section className="mt-8">
             <h2 className="font-heading text-lg font-bold text-foreground">{tx.chooseTopic}</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
               {topics.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setParam({ amne: t.id })}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/50"
                >
                  <span>
                    <span className="block font-semibold text-foreground">{t.label}</span>
                    <span className="block text-sm text-muted-foreground">{t.blurb}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-border bg-card p-4 sm:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground">
               {tx.installTitle}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
               {tx.installBody}
            </p>
            <div className="mt-4">
              <InstallAppGuide />
            </div>
          </section>
        </>
      )}

      <section className="mt-10 rounded-2xl border border-border bg-card p-4 sm:p-6">
         <h2 className="font-heading text-lg font-bold text-foreground">{tx.noAnswer}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
           {tx.contactBody} {user ? tx.contactAuthed : tx.contactGuest}.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
           <Button onClick={() => setOpen(true)}>{tx.sendTicket}</Button>
          {user ? (
            <Link
              to="/mina-arenden"
              className="text-sm font-semibold text-primary hover:underline"
            >
               {tx.myTickets}
            </Link>
          ) : null}
        </div>
      </section>

       {!user && !arende && <GuestTicketBox tx={tx} />}
       {arende && <GuestThread token={arende} tx={tx} lang={lang} />}

      <SupportDialog open={open} onOpenChange={setOpen} defaultSubject={query.trim()} />
      </div>
    </main>

  );
}

function SearchResults({
  tx,
  topicName,
  query,
  results,
  onOpen,
  onAsk,
}: {
  lang: Lang;
  tx: SupportPageCopy;
  topicName: (id: string) => string;
  query: string;
  results: HelpArticle[];
  onOpen: (id: string) => void;
  onAsk: () => void;
}) {
  if (!results.length) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-border p-6 text-center">
        <p className="font-semibold text-foreground">{tx.noResults} ”{query}”</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {tx.noResultsBody}
        </p>
        <Button className="mt-4" onClick={onAsk}>
          {tx.sendTicket}
        </Button>
      </div>
    );
  }
  return (
    <section className="mt-6">
      <p className="text-sm text-muted-foreground">
        {results.length} {tx.results} ”{query}”
      </p>
      <div className="mt-3 space-y-2">
        {results.map((a) => (
          <button
            key={a.id}
            onClick={() => onOpen(a.id)}
            className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/50"
          >
            <p className="font-semibold text-foreground">{a.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{a.summary}</p>
            <p className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {topicName(a.topic)}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

function TopicView({
  topic,
  lang,
  tx,
  topicName,
  onOpen,
  onBack,
}: {
  topic: HelpTopicId;
  lang: Lang;
  tx: SupportPageCopy;
  topicName: (id: string) => string;
  onOpen: (id: string) => void;
  onBack: () => void;
}) {
  const list = articlesByTopic(topic, lang);
  return (
    <section className="mt-6">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> {tx.allTopics}
      </button>
      <h2 className="mt-3 font-heading text-xl font-bold text-foreground">{topicName(topic)}</h2>
      <div className="mt-3 space-y-2">
        {list.map((a) => (
          <button
            key={a.id}
            onClick={() => onOpen(a.id)}
            className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/50"
          >
            <p className="font-semibold text-foreground">{a.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{a.summary}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

function ArticleView({ article, tx, topicName, onBack }: { article: HelpArticle; tx: SupportPageCopy; topicName: (id: string) => string; onBack: () => void }) {
  return (
    <article className="mt-6">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> {topicName(article.topic)}
      </button>
      <h2 className="mt-3 font-heading text-2xl font-bold text-foreground">{article.title}</h2>
      <p className="mt-2 text-muted-foreground">{article.summary}</p>

      <ol className="mt-5 space-y-3">
        {article.steps.map((s, i) => (
          <li key={s} className="flex gap-3">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">
              {i + 1}
            </span>
            <span className="text-foreground">{s}</span>
          </li>
        ))}
      </ol>

      {article.special === "install" && (
        <div className="mt-6">
          <InstallAppGuide />
        </div>
      )}

      {article.links && article.links.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-card p-4">
          <p className="text-sm font-semibold text-foreground">{tx.directLinks}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {article.links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold text-secondary-foreground hover:bg-accent"
              >
                {l.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

/** Genväg för inloggade: pågående ärenden högst upp. */
function MyOpenTickets() {
  const { lang } = useLocale();
  const tx = supportPageText[lang];
  const fetchTickets = useServerFn(getMyTickets);
  const { data } = useQuery({
    queryKey: ["my-tickets"],
    queryFn: () => fetchTickets({}),
    staleTime: 60_000,
  });
  const open = (data ?? []).filter((t) => t.status !== "solved" && t.status !== "spam");
  if (!open.length) return null;
  return (
    <Link
      to="/mina-arenden"
      className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 transition-colors hover:bg-primary/10"
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Ticket className="h-4 w-4 text-primary" />
        {tx.openTickets(open.length)}
      </span>
      <ArrowRight className="h-4 w-4 text-primary" />
    </Link>
  );
}

/** Gäster kan öppna sitt ärende igen genom att klistra in länken från mejlet. */
function GuestTicketBox({ tx }: { tx: SupportPageCopy }) {
  const { lang } = useLocale();
  const navigate = useNavigate();
  const [value, setValue] = useState("");

  const open = () => {
    const raw = value.trim();
    const token = raw.includes("arende=") ? (raw.split("arende=")[1]?.split("&")[0] ?? "") : raw;
    if (!token) return;
    void navigate({ to: localizedPath("/support", lang), search: { arende: token } });
  };

  return (
    <section className="mt-6 rounded-2xl border border-dashed border-border p-4 sm:p-6">
      <h2 className="font-heading text-lg font-bold text-foreground">
        {tx.existingTitle}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {tx.existingBody}
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={tx.pasteLink}
        />
        <Button variant="outline" onClick={open} disabled={!value.trim()}>
          {tx.openTicket}
        </Button>
      </div>
    </section>
  );
}

function GuestThread({ token, tx, lang }: { token: string; tx: SupportPageCopy; lang: Lang }) {
  const fetchThread = useServerFn(getGuestTicket);
  const sendReply = useServerFn(replyGuestTicket);
  const qc = useQueryClient();
  const [body, setBody] = useState("");

  const { data, isPending, error } = useQuery({
    queryKey: ["guest-ticket", token],
    queryFn: () => fetchThread({ data: { token } }),
    refetchInterval: 30000,
  });

  const reply = useMutation({
    mutationFn: () => sendReply({ data: { token, body: body.trim() } }),
    onSuccess: () => {
      setBody("");
      toast.success(tx.replySent);
      void qc.invalidateQueries({ queryKey: ["guest-ticket", token] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending) {
    return <p className="mt-8 text-muted-foreground">{tx.loadingTicket}</p>;
  }
  if (error || !data) {
    return (
      <p className="mt-8 rounded-xl border border-border bg-card p-4 text-muted-foreground">
        {tx.invalidLink}
      </p>
    );
  }

  return (
    <section className="mt-10 rounded-2xl border border-border bg-card p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-bold">
          #{data.ticket.ticket_no} · {data.ticket.subject}
        </h2>
        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
          {statusLabel(data.ticket.status, lang)}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{categoryLabel(data.ticket.category, lang)}</p>

      <ThreadMessages messages={data.messages} tx={tx} />

      <div className="mt-4 space-y-2">
        <Textarea
          rows={3}
          value={body}
          placeholder={tx.replyPlaceholder}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button disabled={body.trim().length < 1 || reply.isPending} onClick={() => reply.mutate()}>
          {reply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {tx.sendReply}
        </Button>
      </div>
    </section>
  );
}

export function ThreadMessages({
  messages,
  tx,
}: {
  tx?: SupportPageCopy;
  messages: {
    id: string;
    author_kind: string;
    author_name: string;
    body: string;
    attachments: string[];
    created_at: string;
  }[];
}) {
  const { lang } = useLocale();
  const copy = tx ?? supportPageText[lang];
  return (
    <div className="mt-4 space-y-3">
      {messages.map((m) => {
        const fromSupport = m.author_kind !== "customer";
        return (
          <div
            key={m.id}
            className={`rounded-xl border p-3 text-sm ${
              fromSupport ? "border-primary/30 bg-primary/5" : "border-border bg-background"
            }`}
          >
            <p className="mb-1 text-xs font-semibold text-muted-foreground">
              {fromSupport ? "Sellavo support" : m.author_name || copy.you} ·{" "}
              {new Date(m.created_at).toLocaleString(copy.locale, {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </p>
            <p className="whitespace-pre-wrap text-foreground">{m.body}</p>
            {m.attachments?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {m.attachments.map((p) => (
                  <a key={p} href={adImageSrc(p)} target="_blank" rel="noreferrer">
                    <img
                      src={adImageSrc(p)}
                      alt={copy.attachment}
                      className="h-20 w-20 rounded-md border border-border object-cover"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
